/**
 * Auto-switch onto the branch an ingest job worked on, and reset a stale
 * persisted active branch back to the baseline.
 *
 * Every `begin*` mutation fulfilled in this tab marks its job as "begun
 * here"; when the jobs-page poll later shows that job terminally
 * successful, the tab adopts the job's branch (`job.branchId`, resolved
 * server-side at begin). Only jobs this tab started can move its active
 * branch — someone else's ingest never yanks the view.
 * Fresh document additions defer their switch until complete so opening a
 * branch at begin cannot unmount the active transfer dialog.
 *
 * Agent applies deliberately don't switch anything: they run on the
 * caller's active branch or are refused (main is protected), so the user
 * is already where the write landed.
 *
 * No `:open` round-trip on adoption — the server opened the job's branch
 * when it resolved it at begin.
 */

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'

import { applyBranchSwitch } from '../../active-branch'
import {
  clearJobBegunHere,
  clearJobEagerSwitch,
  clearJobNeedsCertTrust,
  markJobBegunHere,
  recordJobEagerSwitch,
  resolveCertTrustForJob
} from '../../slices/ingest-slice'
import type { RootState } from '../../store'
import { branchesApi } from './branches-api'
import { ingestApi, mappedTabularIngestApi } from '../ingest'
import { resolveJobBranchAdoptionOutcome } from '../ingest/job-lifecycle'
import { getOntologyBranchHeaderValue } from '../core'

export const branchAdoptionBridge = createListenerMiddleware()

// The active branch persists in localStorage across sessions, but the
// branch itself may be gone by the next load (merged away, deleted, or the
// backing databases were wiped and re-seeded). Whenever a branch list
// arrives, a persisted id that no longer matches an active branch resets
// the tab to the baseline (main) instead of leaving every request pointed
// at a dead database. The reset's own 'Branches' invalidation refetches
// the list once more, which then no-ops here.
branchAdoptionBridge.startListening({
  matcher: branchesApi.endpoints.listBranches.matchFulfilled,
  effect: async (action, api) => {
    // The list is paginated newest-first, so absence from a fetched page no
    // longer implies the branch is gone — the active branch can simply be on a
    // later page (or in the other population). Confirm liveness with a direct
    // id lookup and reset to the baseline only on a definitive miss: a 404
    // (the row is gone — wiped/hard-deleted) or a non-active status (merged or
    // soft-deleted). getBranch hits the same cache entry useActiveBranchRecord
    // already populates, so it is ~free.
    if (!shouldCheckStaleBranch(action.meta.arg.originalArgs)) return
    const activeId = getOntologyBranchHeaderValue()
    if (!activeId) return
    try {
      const branch = await api.dispatch(branchesApi.endpoints.getBranch.initiate(activeId)).unwrap()
      if (branch.status !== 'active') applyBranchSwitch(api.dispatch, undefined)
    } catch (error) {
      if (isDefinitiveMissingBranchError(error)) {
        applyBranchSwitch(api.dispatch, undefined)
      }
    }
  }
})

export function isBranchListSearchArg(arg: unknown): boolean {
  if (!arg || typeof arg !== 'object') return false
  const q = (arg as { q?: unknown }).q
  return typeof q === 'string' && q.trim().length > 0
}

/**
 * Whether a fulfilled listBranches fetch should trigger the stale-active-branch
 * check. Runs on any first, unpaginated, unsearched list load — including the
 * selector's startup `origin: 'user'`/`'system'` fetches, so a stale active
 * branch resets to main on startup and not only when the Settings browser's
 * unfiltered list loads. Skips continuation (offset) and search fetches. The
 * check itself is a direct, origin-agnostic getBranch by id — idempotent and a
 * cache hit — so the few redundant fires per load are ~free.
 */
export function shouldCheckStaleBranch(arg: unknown): boolean {
  if (isBranchListSearchArg(arg)) return false
  return !(arg as { offset?: number })?.offset
}

/** Only a missing row proves a persisted branch is stale; transient failures do not. */
export function isDefinitiveMissingBranchError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'status' in error &&
    (error as { status?: unknown }).status === 404
  )
}

const beginFulfilled = isAnyOf(
  ingestApi.endpoints.beginStructuredJob.matchFulfilled,
  ingestApi.endpoints.beginAvevaGraphicsJob.matchFulfilled,
  ingestApi.endpoints.beginPlcJob.matchFulfilled,
  ingestApi.endpoints.beginRoboticsJob.matchFulfilled,
  ingestApi.endpoints.beginVisionJob.matchFulfilled,
  ingestApi.endpoints.connectMTConnectAgent.matchFulfilled,
  ingestApi.endpoints.connectPI.matchFulfilled,
  ingestApi.endpoints.connectExperionErdb.matchFulfilled,
  ingestApi.endpoints.beginSrsJob.matchFulfilled,
  ingestApi.endpoints.beginSafetyManagerJob.matchFulfilled,
  ingestApi.endpoints.connectSafetyManagerDb.matchFulfilled,
  ingestApi.endpoints.beginUnstructuredJob.matchFulfilled,
  ingestApi.endpoints.beginStructuredDiffJob.matchFulfilled,
  ingestApi.endpoints.uploadStructuredDocument.matchFulfilled,
  ingestApi.endpoints.beginTagImportJob.matchFulfilled,
  ingestApi.endpoints.beginTabularDumpJob.matchFulfilled,
  ingestApi.endpoints.connectSql.matchFulfilled,
  ingestApi.endpoints.connectSoap.matchFulfilled,
  ingestApi.endpoints.runTagMapping.matchFulfilled,
  mappedTabularIngestApi.endpoints.beginMappedTabularIngestJob.matchFulfilled,
  mappedTabularIngestApi.endpoints.beginTabularConfigBuild.matchFulfilled
)

branchAdoptionBridge.startListening({
  matcher: beginFulfilled,
  effect: (action, api) => {
    const { jobId, branchId } = action.payload as {
      jobId?: string
      branchId?: string
    }
    if (jobId) api.dispatch(markJobBegunHere(jobId))
    // Eager checkout: the proxy created+opened the job's branch at begin, so move the
    // active branch onto it immediately — before extraction — whenever it differs from
    // the current branch. On a protected branch (main) this is a real switch onto the
    // fresh ingest branch; on a writable branch the job reuses it (branchId === current)
    // so this no-ops. Begins that carry no branchId fall through to the success-gated
    // adoption below. Remember the branch we left so a non-success outcome can return
    // the user there (see the terminal handler below).
    const fromBranchId = getOntologyBranchHeaderValue()
    if (jobId && branchId && branchId !== fromBranchId) {
      api.dispatch(recordJobEagerSwitch({ jobId, fromBranchId: fromBranchId ?? null }))
      applyBranchSwitch(api.dispatch, branchId)
    }
  }
})

branchAdoptionBridge.startListening({
  matcher: ingestApi.endpoints.beginDocumentAddition.matchFulfilled,
  effect: (action, api) => {
    if (action.payload.mode === 'fresh') {
      api.dispatch(markJobBegunHere(action.payload.jobId))
    }
  }
})

branchAdoptionBridge.startListening({
  matcher: ingestApi.endpoints.completeDocumentAddition.matchFulfilled,
  effect: (action, api) => {
    if (action.payload.mode !== 'fresh' || !action.payload.branchId) return
    applyBranchSwitch(api.dispatch, action.payload.branchId)
    api.dispatch(clearJobBegunHere(action.payload.jobId))
  }
})

branchAdoptionBridge.startListening({
  matcher: ingestApi.endpoints.getIngestJobsPage.matchFulfilled,
  effect: (action, api) => {
    const { jobsBegunHere: tracked, eagerSwitchedFrom } = (api.getState() as RootState).ingest
    if (tracked.length === 0) return
    for (const job of action.payload.jobs) {
      if (!tracked.includes(job.id)) continue
      const outcome = resolveJobBranchAdoptionOutcome(job)
      if (outcome === 'pending') continue
      if (outcome === 'failed') {
        // Cancelled (the branch was reaped server-side) or errored (left
        // half-written): if we eagerly checked out onto this job's branch and
        // the user is still sitting on it, return them to the branch they came
        // from rather than strand them on a dead/partial branch. (To leave the
        // user on an errored branch for inspection instead, gate this on
        // job.state === 'cancelled'.)
        if (
          job.branchId &&
          job.branchId === getOntologyBranchHeaderValue() &&
          Object.prototype.hasOwnProperty.call(eagerSwitchedFrom, job.id)
        ) {
          applyBranchSwitch(api.dispatch, eagerSwitchedFrom[job.id] ?? undefined)
        }
        api.dispatch(clearJobEagerSwitch(job.id))
        api.dispatch(clearJobBegunHere(job.id))
        continue
      }
      // Success — keep the user on the adopted branch (the eager switch already
      // landed them there; this also adopts for jobs begun before the switch).
      api.dispatch(clearJobEagerSwitch(job.id))
      api.dispatch(clearJobBegunHere(job.id))
      if (job.branchId && job.branchId !== getOntologyBranchHeaderValue()) {
        applyBranchSwitch(api.dispatch, job.branchId)
      }
    }
  }
})

// Cancel/stop and job delete may reap the job's branch server-side. Refetch
// the branch list so a reaped branch leaves the selector; the
// stale-active-branch reset above then backstops any tab still pointed at it.
const branchReapFulfilled = isAnyOf(
  ingestApi.endpoints.cancelIngestJob.matchFulfilled,
  ingestApi.endpoints.deleteIngestJobRecord.matchFulfilled
)

branchAdoptionBridge.startListening({
  matcher: branchReapFulfilled,
  effect: (_action, api) => {
    api.dispatch(branchesApi.util.invalidateTags(['Branches']))
  }
})

/**
 * After a GWBK ingest that enabled secured OPC UA scraping finishes, pop a
 * one-time notice telling the operator to trust the proxy's OPC UA client
 * certificate on the gateway — the subscription (and tag readings) stay
 * empty until the quarantined cert is trusted. Tracked at upload time
 * (`markJobNeedsCertTrust`); cleared without a notice on failure.
 */
branchAdoptionBridge.startListening({
  matcher: ingestApi.endpoints.getIngestJobsPage.matchFulfilled,
  effect: (action, api) => {
    const pending = (api.getState() as RootState).ingest.certTrustPending
    if (pending.length === 0) return
    for (const job of action.payload.jobs) {
      if (!pending.some((e) => e.jobId === job.id)) continue
      const state = job.state as string
      if (state === 'error' || state === 'cancelled') {
        api.dispatch(clearJobNeedsCertTrust(job.id))
        continue
      }
      if (resolveJobBranchAdoptionOutcome(job) === 'success') {
        api.dispatch(resolveCertTrustForJob(job.id))
      }
    }
  }
})
